<template>
  <el-dialog
    :visible.sync="visible"
    :width="width"
    :close-on-click-modal="false"
    :show-close="false"
    custom-class="onboarding-dialog"
  >
    <div class="p-4">
      <div class="flex flex-col items-center text-center mb-4">
        <div
          class="w-16 h-16 bg-purple-500 rounded-2xl flex items-center justify-center mb-4"
        >
          <i class="bx bxs-buildings text-3xl text-white"></i>
        </div>
        <h1 class="text-2xl font-bold mb-2">Crea tu taller en Samii</h1>
        <p class="text-gray-500 text-sm">
          Solo necesitamos estos datos para empezar. Podrás
        </p>
        <p class="text-gray-500 text-sm">configurar todo lo demás después.</p>
      </div>

      <el-form
        ref="onboardingForm"
        :model="model"
        :rules="rules"
        v-loading="loading"
      >
        <el-form-item prop="name">
          <label class="block text-sm font-medium mb-2">
            Nombre del negocio <span class="text-red-500">*</span>
          </label>
          <el-input
            v-model="model.name"
            placeholder="Ej: Taller TechFix"
            size="large"
          />
        </el-form-item>

        <el-form-item prop="country">
          <label class="block text-sm font-medium mb-2">
            País <span class="text-red-500">*</span>
          </label>
          <el-select
            v-model="model.country"
            placeholder="Selecciona tu país"
            filterable
            size="large"
            class="w-full"
          >
            <el-option class="h-full" :value="null" disabled>
              <div class="flex flex-col text-center">
                <span class="text-gray-500">---Países Americanos---</span>
              </div>
            </el-option>
            <el-option
              v-for="country in americanCountries"
              :key="country"
              :value="country"
              :label="country"
            >
            </el-option>
            <el-option class="h-full" :value="null" disabled>
              <div class="flex flex-col text-center">
                <span class="text-gray-500">---Países Europeos---</span>
              </div>
            </el-option>
            <el-option
              v-for="country in europeanCountries"
              :key="country"
              :value="country"
              :label="country"
            >
            </el-option>
            <el-option class="h-full" :value="null" disabled>
              <div class="flex flex-col text-center">
                <span class="text-gray-500">---Otros---</span>
              </div>
            </el-option>
            <el-option value="Otro" label="Otro"></el-option>
          </el-select>
        </el-form-item>

        <el-form-item prop="timezone">
          <label class="block text-sm font-medium mb-2">
            Zona horaria <span class="text-red-500">*</span>
          </label>
          <el-select
            v-model="model.timezone"
            placeholder="Selecciona zona horaria"
            filterable
            size="large"
            class="w-full"
          >
            <el-option
              v-for="timezone in timeZones"
              :key="timezone.id"
              :label="timezone.displayName"
              :value="timezone.id"
            >
            </el-option>
          </el-select>
        </el-form-item>

        <div class="pt-4 rounded-lg">
          <div class="flex items-center mb-2">
            <i class="bx bx-bar-chart text-purple-500 text-lg mr-2"></i>
            <label class="text-sm font-medium">
              Cuéntanos un poco sobre tu taller
              <span class="text-gray-400">(opcional)</span>
            </label>
          </div>
        </div>

        <el-form-item prop="hasWorkshop">
          <label class="block text-sm font-medium mb-2">
            ¿Actualmente tienes un taller?
          </label>
          <div class="flex gap-2">
            <el-button
              :type="model.hasWorkshop === 'yes' ? 'primary' : ''"
              :plain="model.hasWorkshop !== 'yes'"
              class="flex-1"
              size="large"
              @click="handleWorkshopChange('yes')"
            >
              Sí
            </el-button>
            <el-button
              :type="model.hasWorkshop === 'no' ? 'primary' : ''"
              :plain="model.hasWorkshop !== 'no'"
              class="flex-1"
              size="large"
              @click="handleWorkshopChange('no')"
            >
              No
            </el-button>
            <el-button
              :type="model.hasWorkshop === 'testing' ? 'primary' : ''"
              :plain="model.hasWorkshop !== 'testing'"
              class="flex-1"
              size="large"
              @click="handleWorkshopChange('testing')"
            >
              Solo estoy probando
            </el-button>
          </div>
        </el-form-item>

        <el-form-item v-if="model.hasWorkshop === 'yes'" prop="workshopCount">
          <label class="block text-sm font-medium mb-2">
            ¿Cuántos talleres tienes?
          </label>
          <div class="flex gap-2">
            <el-button
              :type="model.workshopCount === '1' ? 'primary' : ''"
              :plain="model.workshopCount !== '1'"
              class="flex-1"
              size="large"
              @click="model.workshopCount = '1'"
            >
              1
            </el-button>
            <el-button
              :type="model.workshopCount === '2-3' ? 'primary' : ''"
              :plain="model.workshopCount !== '2-3'"
              class="flex-1"
              size="large"
              @click="model.workshopCount = '2-3'"
            >
              2-3
            </el-button>
            <el-button
              :type="model.workshopCount === '4-10' ? 'primary' : ''"
              :plain="model.workshopCount !== '4-10'"
              class="flex-1"
              size="large"
              @click="model.workshopCount = '4-10'"
            >
              4-10
            </el-button>
            <el-button
              :type="model.workshopCount === '10+' ? 'primary' : ''"
              :plain="model.workshopCount !== '10+'"
              class="flex-1"
              size="large"
              @click="model.workshopCount = '10+'"
            >
              10+
            </el-button>
          </div>
        </el-form-item>
      </el-form>

      <div class="mt-6">
        <el-button
          type="primary"
          size="large"
          class="w-full font-bold"
          :disabled="!isFormReady"
          @click="submit"
        >
          🚀 Entrar a Samii
        </el-button>
      </div>

      <div class="text-center mt-4">
        <label class="text-sm font-medium text-gray-500">
          Podrás personalizar moneda, impuestos, logo, usuarios y políticas más
          adelante.
        </label>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import CatalogService from "@/services/CatalogService";
import BusinessService from "@/services/BusinessService";
import { AmericanCountries, EuropeanCountries } from "@/constants";
import { BUSINESS_UPDATE } from "@/store/modules/business/mutation-types";

export default {
  name: "FirstStepOnboarding",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: "500px",
    },
  },
  data() {
    return {
      loading: false,
      americanCountries: AmericanCountries,
      europeanCountries: EuropeanCountries,
      timeZones: [],
      model: {
        name: "",
        country: null,
        timezone: null,
        hasWorkshop: null,
        workshopCount: null,
      },
      rules: {
        name: [
          {
            required: true,
            message: "El nombre del negocio es requerido",
            trigger: "blur",
          },
        ],
        country: [
          {
            required: true,
            message: "El país es requerido",
            trigger: "change",
          },
        ],
        timezone: [
          {
            required: true,
            message: "La zona horaria es requerida",
            trigger: "change",
          },
        ],
      },
    };
  },
  computed: {
    isFormReady() {
      return (
        this.model.name.trim().length > 0 &&
        this.model.country !== null &&
        this.model.timezone !== null
      );
    },
    visible: {
      get() {
        return this.show;
      },
      set(value) {
        if (!value) {
          this.close();
        }
      },
    },
  },
  async mounted() {
    await this.loadTimeZones();
    this.loadBusinessData();
  },
  methods: {
    close() {
      this.model = {
        name: "",
        country: null,
        timezone: null,
        hasWorkshop: null,
        workshopCount: null,
      };
      this.$refs.onboardingForm?.clearValidate();
      this.$emit("close");
    },
    handleWorkshopChange(value) {
      this.model.hasWorkshop = value;
      if (value !== "yes") {
        this.model.workshopCount = null;
      }
      this.$refs.onboardingForm?.clearValidate();
    },
    async loadTimeZones() {
      try {
        this.timeZones = await CatalogService.getTimeZones();
      } catch (e) {
        this.$emit("error", e);
      }
    },
    loadBusinessData() {
      const business = this.$store.state.business;
      this.model = {
        name: business?.name || "",
        country: business?.country || "",
        timezone: business?.timezone || "",
        hasWorkshop: null,
        workshopCount: null,
      };
      this.$refs.onboardingForm?.clearValidate();
    },
    submit() {
      this.$refs.onboardingForm.validate(async (isValidForm) => {
        if (!isValidForm) return;
        await this.updateBusiness();
      });
    },
    async updateBusiness() {
      if (this.loading) return;
      this.loading = true;
      try {
        // Get current business data to preserve fields not being modified
        const currentBusiness = this.$store.state.business;

        const payload = new FormData();
        payload.append("Id", this.$store.state.business.id);
        // Preserve existing fields
        payload.append("Logo", currentBusiness.logo || "");
        payload.append("Phone", currentBusiness.phone || "");
        payload.append("Address", currentBusiness.address || "");
        payload.append("Currency", currentBusiness.currency || "");
        payload.append("DateFormat", currentBusiness.dateFormat || "");
        payload.append(
          "PhoneCountryCodeId",
          currentBusiness.phoneCountryCodeId || ""
        );
        // Update modified fields
        payload.append("Name", this.model.name);
        payload.append("Country", this.model.country);
        payload.append("Timezone", this.model.timezone);
        payload.append("HasWorkshop", this.model.hasWorkshop || "");
        payload.append("WorkshopCount", this.model.workshopCount || "");
        payload.append("UserOnlyTest", true);

        const business = await BusinessService.update(payload);

        this.$store.commit(BUSINESS_UPDATE, business);

        // Emit completed event to orchestrator
        this.$emit("completed");
      } catch (e) {
        this.$emit("error", e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style>
.el-dialog__wrapper:has(.onboarding-dialog) {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 10vh;
}

.onboarding-dialog {
  margin: 0 !important;
  border-radius: 12px;
}

.onboarding-dialog .el-dialog__header {
  display: none;
}

.onboarding-dialog .el-dialog__body {
  padding: 0;
}

.onboarding-dialog .el-form-item {
  margin-bottom: 12px;
}
</style>
